import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatDisplayDate, getCurrentAESTDate } from '../utils/dateUtils';
import { Users, Calendar, AlertTriangle, CheckCircle, Clock, Plus, CreditCard as Edit, Search, Filter, Download, FileText, Shield, X, Stethoscope, VenetianMask as Mask } from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  phone?: string;
  status: string;
  created_at: string;
  fit_testing_not_applicable?: boolean;
}

interface HealthRecord {
  id: string;
  worker_id: string;
  test_date: string;
  test_type: 'health_monitoring' | 'fit_testing' | 'both';
  next_due_date: string;
  status: 'current' | 'due_soon' | 'overdue';
  health_monitoring_date?: string;
  health_monitoring_due_date?: string;
  health_monitoring_status?: 'current' | 'due_soon' | 'overdue' | 'not_required';
  fit_testing_date?: string;
  fit_testing_due_date?: string;
  fit_testing_status?: 'current' | 'due_soon' | 'overdue' | 'not_required';
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface WorkerWithHealth extends Worker {
  health_record?: HealthRecord;
  health_monitoring_days_until_due?: number;
  fit_testing_days_until_due?: number;
  health_monitoring_overdue?: boolean;
  fit_testing_overdue?: boolean;
}

export const HealthMonitoringPage: React.FC = () => {
  const [workers, setWorkers] = useState<WorkerWithHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<WorkerWithHealth | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [newRecord, setNewRecord] = useState({
    worker_id: '',
    health_monitoring_date: '',
    health_monitoring_renewal_years: 3,
    fit_testing_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchWorkersWithHealthData();
  }, []);

  const fetchWorkersWithHealthData = async () => {
    try {
      // Fetch only active workers who require health monitoring
      const { data: workersData, error: workersError } = await supabase
        .from('workers')
        .select('*')
        .eq('status', 'active')
        .eq('requires_health_monitoring', true)
        .order('name');

      if (workersError) throw workersError;

      // Fetch health records
      const { data: healthData, error: healthError } = await supabase
        .from('health_records')
        .select('*')
        .order('created_at', { ascending: false });

      if (healthError && healthError.code !== 'PGRST116') { // Ignore table doesn't exist error
        console.error('Error fetching health records:', healthError);
      }

      // Combine workers with their latest health records
      const workersWithHealth: WorkerWithHealth[] = (workersData || []).map(worker => {
        // Find the most recent health record for this worker
        const latestRecord = (healthData || []).find(record => record.worker_id === worker.id);
        
        let health_monitoring_days_until_due = 0;
        let fit_testing_days_until_due = 0;
        let health_monitoring_overdue = true; // Default to overdue if no record
        let fit_testing_overdue = worker.fit_testing_not_applicable ? false : true;
        
        if (latestRecord) {
          const today = getCurrentAESTDate();
          
          // Calculate health monitoring status
          if (latestRecord.health_monitoring_due_date) {
            const hmDueDate = new Date(latestRecord.health_monitoring_due_date);
            const hmDiffTime = hmDueDate.getTime() - today.getTime();
            health_monitoring_days_until_due = Math.ceil(hmDiffTime / (1000 * 60 * 60 * 24));
            health_monitoring_overdue = health_monitoring_days_until_due < 0;
          }
          
          // Calculate fit testing status
          if (latestRecord.fit_testing_due_date) {
            const ftDueDate = new Date(latestRecord.fit_testing_due_date);
            const ftDiffTime = ftDueDate.getTime() - today.getTime();
            fit_testing_days_until_due = Math.ceil(ftDiffTime / (1000 * 60 * 60 * 24));
            fit_testing_overdue = fit_testing_days_until_due < 0;
          }
        }

        return {
          ...worker,
          health_record: latestRecord,
          health_monitoring_days_until_due,
          fit_testing_days_until_due,
          health_monitoring_overdue,
          fit_testing_overdue
        };
      });

      setWorkers(workersWithHealth);
    } catch (error) {
      console.error('Error fetching workers with health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newRecord.worker_id || (!newRecord.health_monitoring_date && !newRecord.fit_testing_date)) {
      alert('Please select a worker and at least one test date');
      return;
    }

    setSubmitting(true);
    try {
      // Calculate due dates (12 months from test dates)
      let health_monitoring_due_date = null;
      let fit_testing_due_date = null;
      
      if (newRecord.health_monitoring_date) {
        const hmDate = new Date(newRecord.health_monitoring_date);
        const hmDueDate = new Date(hmDate);
        hmDueDate.setFullYear(hmDueDate.getFullYear() + (newRecord.health_monitoring_renewal_years || 3));
        health_monitoring_due_date = hmDueDate.toISOString().split('T')[0];
      }
      
      if (newRecord.fit_testing_date) {
        const ftDate = new Date(newRecord.fit_testing_date);
        const ftDueDate = new Date(ftDate);
        ftDueDate.setFullYear(ftDueDate.getFullYear() + 1);
        fit_testing_due_date = ftDueDate.toISOString().split('T')[0];
      }

      // Determine test type for backward compatibility
      let test_type: 'health_monitoring' | 'fit_testing' | 'both' = 'health_monitoring';
      if (newRecord.health_monitoring_date && newRecord.fit_testing_date) {
        test_type = 'both';
      } else if (newRecord.fit_testing_date) {
        test_type = 'fit_testing';
      }

      // Use the most recent date for the main test_date field
      const test_date = newRecord.health_monitoring_date && newRecord.fit_testing_date 
        ? (new Date(newRecord.health_monitoring_date) > new Date(newRecord.fit_testing_date) 
           ? newRecord.health_monitoring_date 
           : newRecord.fit_testing_date)
        : (newRecord.health_monitoring_date || newRecord.fit_testing_date);

      // Calculate status for each type
      const today = getCurrentAESTDate();
      
      let health_monitoring_status = 'not_required';
      if (health_monitoring_due_date) {
        const hmDaysUntilDue = Math.ceil((new Date(health_monitoring_due_date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (hmDaysUntilDue < 0) {
          health_monitoring_status = 'overdue';
        } else if (hmDaysUntilDue <= 30) {
          health_monitoring_status = 'due_soon';
        } else {
          health_monitoring_status = 'current';
        }
      }

      let fit_testing_status = 'not_required';
      if (fit_testing_due_date) {
        const ftDaysUntilDue = Math.ceil((new Date(fit_testing_due_date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (ftDaysUntilDue < 0) {
          fit_testing_status = 'overdue';
        } else if (ftDaysUntilDue <= 30) {
          fit_testing_status = 'due_soon';
        } else {
          fit_testing_status = 'current';
        }
      }

      // Overall status is the worst of the two
      let overall_status = 'current';
      if (health_monitoring_status === 'overdue' || fit_testing_status === 'overdue') {
        overall_status = 'overdue';
      } else if (health_monitoring_status === 'due_soon' || fit_testing_status === 'due_soon') {
        overall_status = 'due_soon';
      }

      const recordData = {
        worker_id: newRecord.worker_id,
        test_date,
        test_type,
        next_due_date: health_monitoring_due_date || fit_testing_due_date,
        status: overall_status,
        health_monitoring_date: newRecord.health_monitoring_date || null,
        health_monitoring_due_date,
        health_monitoring_renewal_years: newRecord.health_monitoring_date ? newRecord.health_monitoring_renewal_years : 3,
        health_monitoring_status,
        fit_testing_date: newRecord.fit_testing_date || null,
        fit_testing_due_date,
        fit_testing_status,
        notes: newRecord.notes || null
      };

      const { error } = await supabase
        .from('health_records')
        .insert([recordData]);

      if (error) throw error;

      // Reset form and refresh data
      setNewRecord({
        worker_id: '',
        health_monitoring_date: '',
        health_monitoring_renewal_years: 3,
        fit_testing_date: '',
        notes: ''
      });
      setShowAddModal(false);
      await fetchWorkersWithHealthData();
      
      alert('Health record added successfully!');
    } catch (error) {
      console.error('Error adding health record:', error);
      alert('Failed to add health record. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFitTestingNA = async (worker: WorkerWithHealth) => {
    const newValue = !worker.fit_testing_not_applicable;
    // Optimistic update
    setWorkers(prev => prev.map(w =>
      w.id === worker.id ? { ...w, fit_testing_not_applicable: newValue } : w
    ));
    const { error } = await supabase
      .from('workers')
      .update({ fit_testing_not_applicable: newValue })
      .eq('id', worker.id);
    if (error) {
      // Revert on failure
      setWorkers(prev => prev.map(w =>
        w.id === worker.id ? { ...w, fit_testing_not_applicable: !newValue } : w
      ));
    }
  };

  const getHealthMonitoringStatusColor = (worker: WorkerWithHealth) => {
    if (!worker.health_record?.health_monitoring_date) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (worker.health_monitoring_overdue) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (worker.health_monitoring_days_until_due !== undefined && worker.health_monitoring_days_until_due <= 30) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getFitTestingStatusColor = (worker: WorkerWithHealth) => {
    if (worker.fit_testing_not_applicable) {
      return 'bg-gray-100 text-gray-600 border-gray-200';
    }

    if (!worker.health_record?.fit_testing_date) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (worker.fit_testing_overdue) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (worker.fit_testing_days_until_due !== undefined && worker.fit_testing_days_until_due <= 30) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusIcon = (isOverdue: boolean, daysUntilDue?: number) => {
    if (isOverdue) {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }
    
    if (daysUntilDue !== undefined && daysUntilDue <= 30) {
      return <Clock className="h-4 w-4 text-yellow-600" />;
    }
    
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const getStatusText = (isOverdue: boolean, daysUntilDue?: number, hasRecord?: boolean) => {
    if (!hasRecord) {
      return 'No Record';
    }
    
    if (isOverdue) {
      return `Overdue (${Math.abs(daysUntilDue || 0)} days)`;
    }
    
    if (daysUntilDue !== undefined && daysUntilDue <= 30) {
      return `Due Soon (${daysUntilDue} days)`;
    }
    
    return `Current (${daysUntilDue} days)`;
  };

  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         worker.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesFilter = true;
    if (filterStatus === 'overdue') {
      matchesFilter = worker.health_monitoring_overdue || worker.fit_testing_overdue;
    } else if (filterStatus === 'due_soon') {
      matchesFilter = (!worker.health_monitoring_overdue && (worker.health_monitoring_days_until_due || 0) <= 30) ||
                     (!worker.fit_testing_overdue && (worker.fit_testing_days_until_due || 0) <= 30);
    } else if (filterStatus === 'current') {
      matchesFilter = (!worker.health_monitoring_overdue && (worker.health_monitoring_days_until_due || 0) > 30) ||
                     (!worker.fit_testing_overdue && (worker.fit_testing_days_until_due || 0) > 30);
    }
    
    return matchesSearch && matchesFilter;
  });

  const healthMonitoringOverdueCount = workers.filter(w => !w.health_record?.health_monitoring_date || w.health_monitoring_overdue).length;
  const fitTestingOverdueCount = workers.filter(w => !w.fit_testing_not_applicable && (!w.health_record?.fit_testing_date || w.fit_testing_overdue)).length;
  const healthMonitoringDueSoonCount = workers.filter(w => w.health_record?.health_monitoring_date && !w.health_monitoring_overdue && (w.health_monitoring_days_until_due || 0) <= 30).length;
  const fitTestingDueSoonCount = workers.filter(w => !w.fit_testing_not_applicable && w.health_record?.fit_testing_date && !w.fit_testing_overdue && (w.fit_testing_days_until_due || 0) <= 30).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Health Monitoring & Fit Testing</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track employee health monitoring and respiratory fit testing compliance separately
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Health Record</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="overdue">Overdue</option>
              <option value="due_soon">Due Soon</option>
              <option value="current">Current</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Employees</p>
              <p className="text-xl font-bold text-gray-900">{workers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Stethoscope className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Health Monitoring Overdue</p>
              <p className="text-xl font-bold text-red-600">{healthMonitoringOverdueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Mask className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Fit Testing Overdue</p>
              <p className="text-xl font-bold text-red-600">{fitTestingOverdueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Due Soon (Combined)</p>
              <p className="text-xl font-bold text-yellow-600">{healthMonitoringDueSoonCount + fitTestingDueSoonCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert for overdue employees */}
      {(healthMonitoringOverdueCount > 0 || fitTestingOverdueCount > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-medium text-red-900">
                Health Testing Overdue
              </h3>
              <p className="text-sm text-red-700">
                {healthMonitoringOverdueCount} employees overdue for health monitoring • {fitTestingOverdueCount} employees overdue for fit testing
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Employee Health Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredWorkers.map((worker) => (
          <div
            key={worker.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{worker.name}</h3>
                  <p className="text-sm text-gray-600 capitalize">{worker.role.replace('_', ' ')}</p>
                  <p className="text-xs text-gray-500">{worker.email}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Health Monitoring */}
              <div className={`rounded-lg p-3 border ${getHealthMonitoringStatusColor(worker)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Stethoscope className="h-4 w-4" />
                    <span className="text-sm font-medium">Health Monitoring</span>
                  </div>
                  {getStatusIcon(worker.health_monitoring_overdue || false, worker.health_monitoring_days_until_due)}
                </div>
                
                <div className="space-y-1">
                  <div>
                    <p className="text-xs font-medium">Last Test:</p>
                    <p className="text-sm font-bold">
                      {worker.health_record?.health_monitoring_date 
                        ? formatDisplayDate(worker.health_record.health_monitoring_date)
                        : 'No Record'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium">Next Due:</p>
                    <p className="text-sm font-bold">
                      {worker.health_record?.health_monitoring_due_date 
                        ? formatDisplayDate(worker.health_record.health_monitoring_due_date)
                        : 'Immediate'
                      }
                    </p>
                  </div>
                  
                  <div className="pt-1">
                    <span className="text-xs font-medium">
                      {getStatusText(
                        worker.health_monitoring_overdue || false, 
                        worker.health_monitoring_days_until_due,
                        !!worker.health_record?.health_monitoring_date
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Fit Testing */}
              <div className={`rounded-lg p-3 border ${getFitTestingStatusColor(worker)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Mask className="h-4 w-4" />
                    <span className="text-sm font-medium">Fit Testing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFitTestingNA(worker)}
                      title={worker.fit_testing_not_applicable ? 'Mark fit testing required' : 'Mark fit testing N/A'}
                      className={`text-xs px-2 py-0.5 rounded-full border font-medium transition-colors ${
                        worker.fit_testing_not_applicable
                          ? 'bg-gray-200 text-gray-600 border-gray-300 hover:bg-gray-300'
                          : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      N/A
                    </button>
                    {!worker.fit_testing_not_applicable && getStatusIcon(worker.fit_testing_overdue || false, worker.fit_testing_days_until_due)}
                  </div>
                </div>

                {worker.fit_testing_not_applicable ? (
                  <div className="space-y-1">
                    <div>
                      <p className="text-xs font-medium">Last Test:</p>
                      <p className="text-sm font-bold text-gray-400">N/A</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium">Next Due:</p>
                      <p className="text-sm font-bold text-gray-400">N/A</p>
                    </div>
                    <div className="pt-1">
                      <span className="text-xs font-medium text-gray-400">Not Required</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div>
                      <p className="text-xs font-medium">Last Test:</p>
                      <p className="text-sm font-bold">
                        {worker.health_record?.fit_testing_date
                          ? formatDisplayDate(worker.health_record.fit_testing_date)
                          : 'No Record'
                        }
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium">Next Due:</p>
                      <p className="text-sm font-bold">
                        {worker.health_record?.fit_testing_due_date
                          ? formatDisplayDate(worker.health_record.fit_testing_due_date)
                          : 'Immediate'
                        }
                      </p>
                    </div>

                    <div className="pt-1">
                      <span className="text-xs font-medium">
                        {getStatusText(
                          worker.fit_testing_overdue || false,
                          worker.fit_testing_days_until_due,
                          !!worker.health_record?.fit_testing_date
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedWorker(worker);
                      setNewRecord({
                        worker_id: worker.id,
                        health_monitoring_date: worker.health_record?.health_monitoring_date || '',
                        health_monitoring_renewal_years: 3,
                        fit_testing_date: worker.health_record?.fit_testing_date || '',
                        notes: ''
                      });
                      setShowAddModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                    title="Add/Update Health Record"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-xs">Update</span>
                  </button>
                  
                  <Link
                    to={`/workers/${worker.id}`}
                    className="text-orange-600 hover:text-orange-700 flex items-center space-x-1"
                    title="View Worker Details"
                  >
                    <Users className="h-4 w-4" />
                    <span className="text-xs">View</span>
                  </Link>
                </div>
                
                {/* Overall Status Indicator */}
                <div className="text-right">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    (worker.health_monitoring_overdue || (!worker.fit_testing_not_applicable && worker.fit_testing_overdue)) ? 'bg-red-100 text-red-800' :
                    ((worker.health_monitoring_days_until_due || 0) <= 30 || (!worker.fit_testing_not_applicable && (worker.fit_testing_days_until_due || 0) <= 30)) ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {(worker.health_monitoring_overdue || (!worker.fit_testing_not_applicable && worker.fit_testing_overdue)) ? 'Action Required' :
                     ((worker.health_monitoring_days_until_due || 0) <= 30 || (!worker.fit_testing_not_applicable && (worker.fit_testing_days_until_due || 0) <= 30)) ? 'Due Soon' :
                     'Compliant'}
                  </span>
                </div>
              </div>

              {/* Notes */}
              {worker.health_record?.notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-900 mb-1">Notes:</p>
                  <p className="text-xs text-blue-800">{worker.health_record.notes}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredWorkers.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterStatus ? 'No matching employees found' : 'No employees requiring health monitoring'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterStatus 
              ? 'Try adjusting your search or filter criteria.'
              : 'Enable health monitoring for workers in the Workers section by checking "Requires Health Monitoring & Fit Testing".'
            }
          </p>
          {(searchTerm || filterStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
              }}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
          {!searchTerm && !filterStatus && (
            <Link
              to="/workers"
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Manage Workers
            </Link>
          )}
        </div>
      )}

      {/* Add/Update Health Record Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Add Health Record</h3>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedWorker(null);
                  setNewRecord({
                    worker_id: '',
                    health_monitoring_date: '',
                    health_monitoring_renewal_years: 3,
                    fit_testing_date: '',
                    notes: ''
                  });
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleAddRecord} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee *</label>
                <select
                  value={newRecord.worker_id}
                  onChange={(e) => setNewRecord(prev => ({ ...prev, worker_id: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select an employee</option>
                  {workers.map(worker => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name} - {worker.role.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Stethoscope className="inline h-4 w-4 mr-1" />
                    Health Monitoring Date
                  </label>
                  <input
                    type="date"
                    value={newRecord.health_monitoring_date}
                    onChange={(e) => setNewRecord(prev => ({ ...prev, health_monitoring_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-xs text-gray-500 whitespace-nowrap">Renews in:</label>
                    <select
                      value={newRecord.health_monitoring_renewal_years}
                      onChange={(e) => setNewRecord(prev => ({ ...prev, health_monitoring_renewal_years: parseInt(e.target.value) }))}
                      className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value={1}>1 year</option>
                      <option value={2}>2 years</option>
                      <option value={3}>3 years</option>
                    </select>
                  </div>
                  {newRecord.health_monitoring_date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Due: {new Date(new Date(newRecord.health_monitoring_date).setFullYear(new Date(newRecord.health_monitoring_date).getFullYear() + newRecord.health_monitoring_renewal_years)).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {workers.find(w => w.id === newRecord.worker_id)?.fit_testing_not_applicable ? (
                  <div className="flex items-center justify-center bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-sm text-gray-500 text-center">Fit Testing N/A for this employee</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mask className="inline h-4 w-4 mr-1" />
                      Fit Testing Date
                    </label>
                    <input
                      type="date"
                      value={newRecord.fit_testing_date}
                      onChange={(e) => setNewRecord(prev => ({ ...prev, fit_testing_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    {newRecord.fit_testing_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Due: {new Date(new Date(newRecord.fit_testing_date).setFullYear(new Date(newRecord.fit_testing_date).getFullYear() + 1)).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={newRecord.notes}
                  onChange={(e) => setNewRecord(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Optional notes about the health tests..."
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> You can add either health monitoring, fit testing, or both.
                  Health monitoring renews every 1, 2, or 3 years (select above), fit testing renews every 12 months.
                </p>
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedWorker(null);
                    setNewRecord({
                      worker_id: '',
                      health_monitoring_date: '',
                      health_monitoring_renewal_years: 3,
                      fit_testing_date: '',
                      notes: ''
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Saving...' : 'Add Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Compliance Summary */}
      {workers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Health Compliance Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {Math.round(((workers.length - healthMonitoringOverdueCount) / workers.length) * 100)}%
              </div>
              <div className="text-sm text-gray-600">Health Monitoring Compliance</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(() => { const applicable = workers.filter(w => !w.fit_testing_not_applicable); return applicable.length ? Math.round(((applicable.length - fitTestingOverdueCount) / applicable.length) * 100) : 100; })()}%
              </div>
              <div className="text-sm text-gray-600">Fit Testing Compliance</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {workers.filter(w => w.health_record?.health_monitoring_date).length}
              </div>
              <div className="text-sm text-gray-600">Have Health Records</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {workers.filter(w => w.health_record?.fit_testing_date).length}
              </div>
              <div className="text-sm text-gray-600">Have Fit Test Records</div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Only showing workers who require health monitoring and fit testing. 
              Other workers can be configured in the Workers section by checking "Requires Health Monitoring & Fit Testing".
            </p>
          </div>
        </div>
      )}
    </div>
  );
};